/**
 * Persistent glossary (§7.1). Every term the console uses that a newcomer would not know, defined
 * in the operator's language rather than a textbook's.
 */

export interface Term {
  id: string;
  term: string;
  short: string;
  body: string;
}

export const TERMS: Term[] = [
  {
    id: 'headwater',
    term: 'Headwater',
    short: 'The reservoir surface elevation, in metres above sea level.',
    body: 'The water level on the upstream side of the dam. Everything else follows from it: how much head the machines have, how much storage is left, and how far you are from losing control of the release.',
  },
  {
    id: 'forebay',
    term: 'Forebay',
    short: 'The reservoir immediately upstream of the intakes.',
    body: 'On a storage project the forebay and the reservoir are effectively the same water. On a run-of-river plant it is the small pond the intakes draw from.',
  },
  {
    id: 'tailwater',
    term: 'Tailwater',
    short: 'The river surface elevation immediately below the plant.',
    body: 'It rises with total release, and it subtracts from your head. At a low-head plant this is decisive: pass enough water and the tailwater rises far enough to take a quarter of your generation away.',
  },
  {
    id: 'freeboard',
    term: 'Freeboard',
    short: 'The vertical distance from the water surface to the dam crest.',
    body: 'The margin you spend for revenue and defend for safety. Holding a high pool earns more per cubic metre and leaves less room for the flood you did not forecast.',
  },
  {
    id: 'surcharge',
    term: 'Surcharge',
    short: 'Storage above full supply level, up to the maximum flood level.',
    body: 'Emergency space, not operating space. Using it is legitimate during declared flood operations and expensive otherwise — $50k an hour until you declare.',
  },
  {
    id: 'rule-curve',
    term: 'Rule curve',
    short: 'The seasonal target elevation, with a band around it.',
    body: 'Where the pool should be for the time of year: low before the freshet so there is room for it, full afterwards so there is water for winter. Above the band is flood-storage encroachment; below it is a refill risk. Both cost $3k per metre-day.',
  },
  {
    id: 'wicket-gate',
    term: 'Wicket gate',
    short: 'The adjustable vanes that meter water into a turbine runner.',
    body: 'The governor moves them to hold a load setpoint. Their travel maps to flow through a monotone curve, so 50 % travel is not 50 % flow.',
  },
  {
    id: 'tainter-gate',
    term: 'Tainter (radial) gate',
    short: 'A curved spillway gate that pivots on trunnions.',
    body: 'Cheap to hoist because the water load passes through the pivot. At Kettle Narrows they take 53 minutes to travel from shut to full, which is why spillway decisions are made an hour before they are needed.',
  },
  {
    id: 'tdg',
    term: 'TDG — total dissolved gas',
    short: 'Percentage saturation of dissolved gas in the water.',
    body: 'Spilling water entrains air and drives gas into solution under the pressure of the plunge pool. Above about 110 % it causes gas-bubble trauma in fish, which is why the licence caps it. It depends on discharge per metre of gate width, so spreading spill across more gates is the most effective thing you can do.',
  },
  {
    id: 'ramping',
    term: 'Ramping rate',
    short: 'How fast the river stage is allowed to change.',
    body: 'Limits are on tailwater stage, not flow: 0.10 m/h down, 0.30 m/h up. Down-ramping too fast strands fish and undercuts banks. In flow terms it is roughly 100 m³/s per hour — about a third of one unit.',
  },
  {
    id: 'water-value',
    term: 'Water value',
    short: 'What the next MWh of stored water is worth, in $/MWh.',
    body: 'A decision aid, not an instruction. It rises when the pool is below the rule curve — water you are short of is worth more than the average price — and falls when you are full. Sell when the price beats it.',
  },
  {
    id: 'snl',
    term: 'SNL — speed no load',
    short: 'A unit spinning at synchronous speed making no power.',
    body: 'The state between opening the intake gate and closing the breaker. It passes about 15 m³/s at Kettle Narrows and produces nothing.',
  },
  {
    id: 'spinning-reserve',
    term: 'Spinning reserve',
    short: 'Headroom on synchronised machines, ready in seconds.',
    body: 'Σ(P_max − P) over your online units. Four units at full load carry none; two at 200 MW carry 200 MW. Paid $6/MW·h when the obligation is met and charged $60/MW·h when it is not.',
  },
  {
    id: 'agc',
    term: 'AGC — automatic generation control',
    short: 'Following a grid regulation signal around your schedule.',
    body: 'The dispatcher moves you ±50 MW second by second to balance the system. Paid for the range you provide, so it is only worth taking when you have headroom both ways.',
  },
  {
    id: 'eap',
    term: 'EAP — emergency action plan',
    short: 'The graduated dam-safety declaration, levels 1 to 3.',
    body: 'Level 1 opens the incident log. Level 2 notifies the regulator and the town. Level 3 is an evacuation notice, and it is declared automatically above the maximum flood level. Declaring early costs little; declaring late is what turns a safety grade from A to F.',
  },
  {
    id: 'rough-zone',
    term: 'Rough zone',
    short: 'A load band where a Francis runner vibrates badly.',
    body: 'Between 20 % and 35 % of rated output, vortex shedding in the draft tube shakes the machine. Half an hour in the zone starts heating the guide bearing. If you need less than minimum load, you need one fewer unit.',
  },
  {
    id: 'frazil',
    term: 'Frazil ice',
    short: 'Ice crystals forming in supercooled turbulent water.',
    body: 'On clear cold nights it adheres to trash racks and intakes and derates the machines. Flow through the intake is what stops it sticking, so keeping units loaded overnight is the defence — shutting them down to dodge a negative price is how you arrive at the morning peak derated.',
  },
  {
    id: 'llo',
    term: 'LLO — low-level outlet',
    short: 'A deep sluice that discharges below the spillway sill.',
    body: 'Works at any pool level, which the spillway does not, and travels faster. The drawdown tool of last resort. Its discharge is violently aerated, so it enters the river at 125 % TDG.',
  },
  {
    id: 'mol',
    term: 'MOL / FSL / MFL',
    short: 'Minimum operating, full supply, and maximum flood levels.',
    body: 'MOL is where the intakes start drawing vortices and the units trip. FSL is the top of normal operation. MFL is the top of the surcharge — above it you are in an emergency and $2M an hour.',
  },
  {
    id: 'freshet',
    term: 'Freshet',
    short: 'The spring melt flood.',
    body: 'The year’s water arrives over a few weeks. Refill too fast and the second peak spills; too slowly and you finish the season below the curve with nothing upstream left to catch up with.',
  },
  {
    id: 'muskingum',
    term: 'Muskingum routing',
    short: 'How a flood wave travels and flattens down a river reach.',
    body: 'Your release does not arrive at the gauge instantly or intact. At Kettle Narrows it takes about 5.5 hours to reach Ferrisburg and the peak arrives lower and wider than it left.',
  },
];

export function findTerm(id: string): Term | undefined {
  return TERMS.find((t) => t.id === id);
}
